import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Paper,
    Radio,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { HEADER, NAV } from 'src/layouts/config-layout';
import { useNavPinned } from 'src/layouts/dashboard/nav-pinned-context';
import HorizontalPager from 'src/components/horizontal-pager';
import { CountryBarProps, CountryItem } from './types';
import { ALL_SENTINEL, toggleCountrySelection } from './countrySelection';
import { useGetBQCountriesQuery } from 'src/context/api';
import Iconify from 'src/components/iconify';


// Component to handle icon loading with loader
function IconifyWithLoader({ icon, width, height }: { icon: string; width: number; height: number }) {
    const [isLoaded, setIsLoaded] = useState(false);
    const iconRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        // Reset loading state when icon changes
        setIsLoaded(false);

        // Check if icon is already loaded
        const checkIconLoaded = () => {
            if (iconRef.current) {
                const svg = iconRef.current.querySelector('svg');
                if (svg) {
                    setIsLoaded(true);
                }
            }
        };

        // Use MutationObserver to detect when the icon SVG is injected into the DOM
        if (iconRef.current) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        checkIconLoaded();
                    }
                });
            });

            observer.observe(iconRef.current, {
                childList: true,
                subtree: true,
            });

            // Also check immediately in case it's already loaded
            checkIconLoaded();

            // Fallback timeout to show icon after 2 seconds even if not detected
            const timeout = setTimeout(() => {
                setIsLoaded(true);
            }, 2000);

            return () => {
                observer.disconnect();
                clearTimeout(timeout);
            };
        }
    }, [icon]);

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                width: width,
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {!isLoaded && (
                <CircularProgress
                    size={10}
                    thickness={5}
                    sx={{
                        color: '#919EAB',
                    }}
                />
            )}
            <Box
                ref={iconRef}
                sx={{
                    display: isLoaded ? 'inline-flex' : 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon={icon} width={width} height={height} />
            </Box>
        </Box>
    );
}
const ALL_OPTION: CountryItem = { country_code: ALL_SENTINEL, country_name: 'All' };

export function CountryBar(props: CountryBarProps) {
    const {
        countries,
        renderCountryLabel,
        stickyTop,
        loading,
        topGap = 15,
        contentPaddingX,
        selectedColor,
        selectedBorderWidth,
        reserveGapWhenHidden = false,
        hiddenGap = 15,
        tabSpacing,
    } = props;
    const theme = useTheme();
    const navPinned = useNavPinned();
    const navWidth = navPinned ? NAV.W_VERTICAL_EXPANDED : NAV.W_VERTICAL;
    const { data: baseCountries }: any = useGetBQCountriesQuery(void 0);

    const includeAll = props.multiple ? true : !!props.includeAll;
    const indicator: 'underline' | 'radio' | 'checkbox' = props.multiple
        ? 'checkbox'
        : (props.indicator ?? 'underline');
    const enableDeepLinking = props.multiple ? false : (props.enableDeepLinking ?? true);
    const singleSelectedCountry: string | null = props.multiple ? null : props.selectedCountry;
    const multiSelectedCountries: string[] = props.multiple ? props.selectedCountries : [];

    // Radio / checkbox bars pin the "All" tab outside the pager: it stays visible while
    // paging through the countries, and the left chevron lands right after its separator.
    // Underline bars keep All inside the pager (preprod layout untouched).
    const pinAll = includeAll && indicator !== 'underline';
    const items = includeAll && !pinAll ? [ALL_OPTION, ...countries] : countries;
    const allCountryNames = useMemo(() => countries.map((c) => c.country_name), [countries]);

    const getCountryCodeFromRegionName = (regionName: string, countryList: any[]): string | null => {
        if (!countryList || countryList.length === 0) return null;
        const exactMatch = countryList.find(
            (c) => c.country_name?.toLowerCase() === regionName?.toLowerCase()
        );
        if (exactMatch) return exactMatch.country_code;
        const partialMatch = countryList.find(
            (c) => regionName?.toLowerCase().includes(c.country_name?.toLowerCase()) ||
                c.country_name?.toLowerCase().includes(regionName?.toLowerCase())
        );
        if (partialMatch) return partialMatch.country_code;
        if (regionName && regionName.length === 2) return regionName.toUpperCase();
        return null;
    };

    const codeToName = useMemo(() => {
        const map = new Map<string, string>();
        countries.forEach((c: any) => {
            map.set(String(c.country_code).toUpperCase(), c.country_name);
        });
        return map;
    }, [countries]);

    // "All" checked is its own state, distinct from "every country checked by hand":
    // both filter on the same countries, but only the first shows the All box checked.
    // Null means untouched, in which case a full selection (the default restored by the
    // pages when nothing is persisted) reads as All.
    const [allModeOverride, setAllModeOverride] = useState<boolean | null>(null);
    const everyCountrySelected =
        allCountryNames.length > 0 && multiSelectedCountries.length === allCountryNames.length;
    const allMode = allModeOverride ?? everyCountrySelected;

    const [didApplyDeepLink, setDidApplyDeepLink] = useState(false);

    useEffect(() => {
        if (props.multiple) return;
        if (didApplyDeepLink || !enableDeepLinking) return;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const countryName = codeToName.get(codeParam.toUpperCase());
                if (countryName && countryName !== props.selectedCountry) {
                    props.onCountryChange(countryName);
                }
            }
        } catch { }
        setDidApplyDeepLink(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeToName, didApplyDeepLink, enableDeepLinking]);

    const loadingItems = Array.from({ length: 10 }, (_, index) => (
        <Stack key={index} direction="row" gap={0.75} width="10%">
            <Skeleton variant="rounded" width={"12.5%"} animation="wave" />
            <Skeleton variant="rectangular" width={"90%"} animation="wave" />
        </Stack>
    ));

    if (countries.length <= 1 && (!loading || reserveGapWhenHidden)) {
        return reserveGapWhenHidden ? <Box sx={{ height: hiddenGap }} /> : null;
    }

    const isItemSelected = (c: CountryItem): boolean => {
        const isAll = c.country_code === ALL_SENTINEL;
        if (props.multiple) {
            return isAll ? allMode : !allMode && multiSelectedCountries.includes(c.country_name);
        }
        return isAll ? singleSelectedCountry === 'ALL' : singleSelectedCountry === c.country_name;
    };

    const activeIndex = props.multiple
        ? 0
        : includeAll
            ? (singleSelectedCountry === 'ALL'
                ? 0
                : items.findIndex((c: any) => c.country_name === singleSelectedCountry))
            : (singleSelectedCountry
                ? countries.findIndex((c: any) => c.country_name === singleSelectedCountry)
                : 0);

    const handleItemClick = (c: CountryItem) => {
        const isAll = c.country_code === ALL_SENTINEL;
        if (props.multiple) {
            const next = toggleCountrySelection(
                multiSelectedCountries,
                allCountryNames,
                isAll ? ALL_SENTINEL : c.country_name,
                allMode
            );
            setAllModeOverride(isAll);
            props.onCountriesChange(next);
            return;
        }
        if (isAll) {
            props.onCountryChange('ALL');
            return;
        }
        if (singleSelectedCountry === c.country_name) return;
        props.onCountryChange(c.country_name);
    };

    /** One country tab (also used for the pinned "All" tab, outside the pager). */
    const renderTab = (c: CountryItem, isFirst: boolean) => {
        const isAll = c.country_code === ALL_SENTINEL;
        const isSelected = isItemSelected(c);
        const countryCode = isAll
            ? null
            : getCountryCodeFromRegionName(c.country_name, baseCountries);
        // Radio / checkbox selectors show the ISO code only ("FR"), the full
        // name being redundant next to the flag. Underline tabs keep the name.
        const resolvedCode = !isAll
            ? (c.country_code?.length === 2 ? c.country_code : countryCode)
            : null;
        const defaultLabel = indicator !== 'underline' && resolvedCode
            ? resolvedCode.toUpperCase()
            : c.country_name;

        return (
            <Button
                key={c.country_code}
                variant="text"
                color="inherit"
                disableRipple
                disableFocusRipple
                disableTouchRipple
                onClick={() => handleItemClick(c)}
                sx={{
                    pl: contentPaddingX != null && isFirst ? 0 : '6px',
                    pr: isAll ? '18px' : '6px',
                    mr: isAll ? '6px' : 0,
                    position: 'relative',
                    '&::after': isAll
                        ? {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '18px',
                            width: '1px',
                            backgroundColor: theme.palette.divider,
                        }
                        : undefined,
                    py: '14px',
                    height: '42px',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        opacity: .7,
                    },
                    width: 'fit-content',
                    background: "none",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    borderRadius: 0,
                    borderBottom: indicator === 'underline'
                        ? (isSelected
                            ? `${selectedBorderWidth ?? 3}px solid ${selectedColor ?? 'black'}`
                            : '2px solid transparent')
                        : 'none',
                    boxSizing: 'border-box',
                    alignSelf: 'stretch',
                }}
            >
                {indicator === 'radio' && (
                    <Radio checked={isSelected} size="small" disableRipple sx={{ p: 0 }} tabIndex={-1} />
                )}
                {indicator === 'checkbox' && (
                    <Checkbox checked={isSelected} size="small" disableRipple sx={{ p: 0 }} tabIndex={-1} />
                )}

                {!isAll && (
                    <IconifyWithLoader icon={`circle-flags:${countryCode}`} width={16} height={16} />
                )}

                <Typography
                    sx={{
                        fontWeight: 400,
                        fontSize: ".875rem",
                    }}
                    color={isSelected ? "black" : "#919EAB"}
                >
                    {renderCountryLabel
                        ? renderCountryLabel(c, isSelected)
                        : defaultLabel}
                </Typography>
            </Button>
        );
    };

    /** "Clear all" — drops the country filter, i.e. back to the All state. */
    const clearAll = props.multiple ? (
        <Button
            variant="text"
            color="inherit"
            disableRipple
            startIcon={<Iconify icon="mdi:close" width={18} height={18} />}
            onClick={() => {
                setAllModeOverride(true);
                props.onCountriesChange([...allCountryNames]);
            }}
            disabled={allMode}
            sx={{
                flex: '0 0 auto',
                ml: '8px',
                px: '12px',
                height: '32px',
                borderRadius: '8px',
                bgcolor: 'rgba(145, 158, 171, 0.12)',
                fontWeight: 500,
                fontSize: '.875rem',
                whiteSpace: 'nowrap',
                '& .MuiButton-startIcon': { mr: '6px' },
                '&:hover': { bgcolor: 'rgba(145, 158, 171, 0.24)' },
            }}
        >
            Clear all
        </Button>
    ) : null;

    const pager = (
        <HorizontalPager
            items={items}
            getItemKey={(c) => c.country_code}
            activeIndex={activeIndex}
            spacing={tabSpacing}
            noArrowGutter={contentPaddingX != null}
            // Radio / checkbox bars keep their chevrons visible (disabled when there is
            // nothing to page through): with ISO-code labels the tabs often fit on a
            // single page, and the arrows are a wanted landmark.
            alwaysShowArrows={indicator !== 'underline'}
            renderItem={(c: CountryItem, index: number) => renderTab(c, !pinAll && index === 0)}
        />
    );

    return (
        <>
            <Box sx={{ height: topGap }} />
            <Paper
                sx={{
                    p: 0,
                    px: contentPaddingX,
                    height: '42px',
                    // `position: fixed` + `left: navWidth` + `width: 100%` is over-constrained:
                    // `right` is ignored, so the bar is 100vw wide starting after the nav and
                    // overflows the viewport by the nav width — anything anchored to its right
                    // edge (the pager's next-page chevron) ends up off screen. The pinned-All
                    // bars therefore get the real width; the others keep the legacy value,
                    // which the pager compensates with its 93%/82% widths.
                    width: pinAll ? { xs: '100%', lg: `calc(100% - ${navWidth}px)` } : '100%',
                    bgcolor: '#F7F7F7',
                    overflow: 'hidden',
                    borderBottom: '1px solid #EAEAEA',
                    position: 'fixed',
                    top: stickyTop ?? HEADER.H_DESKTOP * 2,
                    left: { lg: navWidth },
                    right: 0,
                    borderRadius: 0,
                    zIndex: theme.zIndex.appBar - 1,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {loading ?
                    (
                        <Box display="flex" flexDirection="row" px={contentPaddingX != null ? 0 : 5} alignItems="center" width="100%" gap={1.5}>
                            {loadingItems}
                        </Box>
                    ) : pinAll ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, height: '42px' }}>
                            {renderTab(ALL_OPTION, true)}
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>{pager}</Box>
                            {clearAll}
                        </Box>
                    ) : pager}
            </Paper>
        </>
    );
}
